import type {
  FundingRateFrequency,
  FundingRateMAWindow,
  FundingRateScope,
  FundingRateTimeRange,
} from './HistoricalFundingRate.types';

export const FUNDING_RATE_DEFAULT_SCOPE: FundingRateScope = 'asset-level';

export const FUNDING_RATE_DEFAULT_TIME_RANGE: FundingRateTimeRange = '3M';

export const FUNDING_RATE_TIME_RANGE_DAYS: Record<FundingRateTimeRange, number | null> = {
  '1D': 1,
  '1W': 7,
  '1M': 30,
  '3M': 90,
  '1Y': 365,
  All: null,
};

export const FUNDING_RATE_DEFAULT_MA_WINDOW: FundingRateMAWindow = 0;

export const FUNDING_RATE_MA_WINDOW_LABELS: Record<FundingRateMAWindow, string> = {
  0: 'Off',
  7: '7D',
  14: '14D',
  30: '30D',
};

export const FUNDING_RATE_DEFAULT_FREQUENCY: FundingRateFrequency = '1d';

export const FUNDING_RATE_FREQUENCY_LABELS: Record<FundingRateFrequency, string> = {
  '1h': '1H',
  '1d': '1D',
};

/**
 * Ranges for which hourly frequency is disallowed. Funding publishes every 8h, so hourly sampling
 * adds no signal, and 1h over these ranges (up to ~219k points across 5 exchanges) exceeds the
 * chart's render budget and crashes. Kept as data so the toolbar and the persisted-state guard agree.
 */
export const FUNDING_RATE_HOURLY_DISABLED_RANGES: readonly FundingRateTimeRange[] = ['1Y', 'All'];

/** True when the (frequency, range) pair is not allowed — hourly on a long range. */
export function isFundingRateFrequencyRangeDisallowed(
  frequency: FundingRateFrequency,
  range: FundingRateTimeRange
): boolean {
  return frequency === '1h' && FUNDING_RATE_HOURLY_DISABLED_RANGES.includes(range);
}

/** Maximum number of exchanges a user can select simultaneously in the By Exchange scope. */
export const FUNDING_RATE_MAX_EXCHANGES = 5;

export const FUNDING_RATE_METRIC = 'futures_aggregate_funding_rate_usd_margin_8h_period';
export const FUNDING_RATE_PRICE_METRIC = 'ReferenceRateUSD';

/** Display label for `FUNDING_RATE_METRIC` in the "key doesn't have access to X" message. */
export const FUNDING_RATE_METRIC_LABEL = 'Funding Rate';
/** Display label for `FUNDING_RATE_PRICE_METRIC` in the "key doesn't have access to X" message. */
export const FUNDING_RATE_PRICE_METRIC_LABEL = 'Price';

export const FUNDING_RATE_PRIMARY_AXIS_LABEL = 'Funding Rate (%)';
export const FUNDING_RATE_SECONDARY_AXIS_LABEL = 'Price (USD)';

/** Series name used for the single aggregated funding rate line in asset-level scope. */
export const FUNDING_RATE_AGGREGATED_SERIES_NAME = 'Aggregated Funding Rate';
